import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../apiHelpers';
import { instance } from '../instance';
import ScannerHome from './ScannerHome';
import useScannerStore from '../stores/useScannerStore';
import useUserStore from '../stores/useUserStore';
import { useDeviceMode } from './useDeviceMode';
import { buzz, inShell, readScannerId } from './shell';
import { shellTokens } from './shellTokens';
import useScannerInput from './hooks/useScannerInput';
import { useIdleUpdate } from './useIdleUpdate';
import ShellStatus from './ShellStatus';
import UserPicker from '../components/UserPicker';
import { toolsFor } from './tools';
import { BackIcon, LockIcon } from './icons';
import { BAR_KEY } from './ui';
import type { ToolContext } from './types';
// Shop-floor scanner debug log (047).
import { setDiagnosticOperator, logScannerDiagnostic } from './diagnostics';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// Badge barcodes encode an operator email. Defined at module scope so its
// identity is stable across renders — an inline arrow would re-run all of
// useScannerInput's listener-binding effects on every render. Doubles as the
// "scan complete" signal: only fires once a full address is present.
const isEmailBadge = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

// =========================================================================
// PIN PAD COMPONENT
// =========================================================================

interface PinPadProps {
  onSubmit: (pin: string) => void;
  error: string | null;
  loading: boolean;
}

const PinPad: React.FC<PinPadProps> = ({ onSubmit, error, loading }) => {
  const [pin, setPin] = useState('');

  // Reset PIN when error changes so user can retry
  useEffect(() => {
    if (error) {
      setPin('');
    }
  }, [error]);

  const handleDigit = (digit: string) => {
    if (pin.length < 4) {
      const next = pin + digit;
      setPin(next);
      if (next.length === 4) {
        onSubmit(next);
      }
    }
  };

  const handleBackspace = () => {
    setPin((p) => p.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* PIN dots */}
      <div className="flex gap-3 mb-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full border-2 transition-colors ${
              i < pin.length
                ? 'bg-blue-400 border-blue-400'
                : 'border-outline bg-transparent'
            }`}
          />
        ))}
      </div>

      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}

      {loading && (
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      )}

      {/* Number grid */}
      <div className="grid grid-cols-3 gap-3 w-64">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
          <button
            key={digit}
            onClick={() => handleDigit(String(digit))}
            disabled={loading || pin.length >= 4}
            className="bg-gray-700 hover:bg-gray-750 active:bg-outline text-white text-2xl font-bold py-4 rounded-xl transition-colors disabled:opacity-50"
          >
            {digit}
          </button>
        ))}
        <button
          onClick={handleClear}
          disabled={loading}
          className="bg-gray-800 hover:bg-gray-700 text-on-surface-variant text-sm font-medium py-4 rounded-xl transition-colors"
        >
          CLR
        </button>
        <button
          onClick={() => handleDigit('0')}
          disabled={loading || pin.length >= 4}
          className="bg-gray-700 hover:bg-gray-750 active:bg-outline text-white text-2xl font-bold py-4 rounded-xl transition-colors disabled:opacity-50"
        >
          0
        </button>
        <button
          onClick={handleBackspace}
          disabled={loading}
          className="bg-gray-800 hover:bg-gray-700 text-on-surface-variant text-xl py-4 rounded-xl transition-colors"
        >
          &#9003;
        </button>
      </div>
    </div>
  );
};

// =========================================================================
// OPERATOR LOGIN GATE
// =========================================================================

const LAST_OPERATOR_KEY = 'scanner.lastOperatorId';

// A locked-down kiosk throws on storage access; a remembered name must never block sign-in.
const readLastOperator = () => {
  try { return window.localStorage.getItem(LAST_OPERATOR_KEY); } catch { return null; }
};
const writeLastOperator = (id: string) => {
  try { window.localStorage.setItem(LAST_OPERATOR_KEY, id); } catch { /* best effort */ }
};

interface ScannerOperator {
  id: string;
  display_name: string;
}

interface OperatorLoginGateProps {
  onAuthenticated: (user: any, operatorToken: string | undefined) => void;
}

export const OperatorLoginGate: React.FC<OperatorLoginGateProps> = ({ onAuthenticated }) => {
  const api = useApi();
  const [operators, setOperators] = useState<ScannerOperator[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [step, setStep] = useState<'badge' | 'pin'>('badge');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Each attempt clears the error first, so a second wrong PIN buzzes again.
  useEffect(() => { if (error) buzz('reject'); }, [error]);

  // The names a person can pick instead of scanning a badge. Without the list
  // the badge scan is the whole gate, as it always was.
  // Retried until it lands: on a kiosk scanner the page often loads before the
  // shell has its first device token (right after boot), and one failed fetch
  // used to leave the name picker missing until the next reload.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = () => {
      api.get('/scanner/operators', { silent: true })
        .then((rows: unknown) => { if (alive && Array.isArray(rows)) setOperators(rows as ScannerOperator[]); })
        .catch(() => { if (alive) timer = setTimeout(load, 5000); });
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseOperator = (op: ScannerOperator) => {
    setError(null);
    setUserId(op.id);
    setEmail('');
    setUserName(op.display_name);
    setStep('pin');
  };

  // UserPicker reads firstname and lastname; an operator is one display name.
  const pickerUsers = operators.map((o) => ({ id: o.id, firstname: o.display_name, lastname: '' }));
  const lastOperatorId = readLastOperator();
  const lastOperator = operators.find((o) => o.id === lastOperatorId) || null;

  // Badge scans arrive as an EMAIL string. On rugged Android handhelds the
  // scanner delivers via InputConnection (an `input` event), NOT raw key
  // events — so the badge input is driven by the same useScannerInput hook
  // the Assembly/Marker modes use, which captures all three delivery paths
  // (InputConnection batch, keyboard-wedge Enter, USB HID at document scope).
  // The previous keydown-only handler silently dropped InputConnection scans.
  const handleBadgeScan = useCallback(async (scanned: string) => {
    if (!scanned) return;

    setError(null);
    setLoading(true);

    try {
      const result: any = await api.post('/scanner/auth/validate', {
        body: { email: scanned },
        silent: true,
      });

      if (result.valid) {
        setEmail(scanned);
        setUserId(null);
        setUserName(`${result.user.firstname} ${result.user.lastname}`);
        setStep('pin');
      } else {
        const badgeErrorMap: Record<string, string> = {
          INVALID_CREDENTIALS: 'Badge not recognized',
        };
        setError(badgeErrorMap[result.error] || result.error || 'Badge not recognized');
      }
    } catch (err: any) {
      // Say WHICH failure. "Failed to validate badge" was the same sentence for
      // an expired session, a network drop and a server error, which made a
      // shop-floor report impossible to act on without a device in hand.
      const status = err?.status;
      setError(
        status === 401 ? (inShell()
            ? 'This scanner is not signed in. Ask a manager to check it.'
            : 'Session expired on this device. Sign in to Firefleet again.')
          : status ? `Badge check failed (${status})`
          : 'No connection to the server'
      );
      logScannerDiagnostic({
        surface: 'badge',
        event: 'validate_error',
        detail: { status: status ?? null, message: err?.message || String(err) },
      });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive the hidden badge input. Disabled during PIN entry so it doesn't
  // steal focus from the pad (and so the document-level key listener is torn
  // down — PIN digits are never captured).
  const { inputRef: badgeInputRef } = useScannerInput({
    onScan: handleBadgeScan,
    // The badge raw value is an EMAIL and is redacted
    // server-side; only its shape reaches the table.
    surface: 'badge',
    minLength: 5,
    validator: isEmailBadge,
    disabled: step !== 'badge',
  });

  const handlePinSubmit = useCallback(async (pin: string) => {
    setError(null);
    setLoading(true);

    try {
      const result: any = await api.post('/scanner/auth/verify-pin', {
        body: userId ? { user_id: userId, pin } : { email, pin },
        silent: true,
      });

      if (result.valid) {
        if (result.user?.id) writeLastOperator(result.user.id);
        onAuthenticated(result.user, result.operator_token);
      } else {
        const errorMap: Record<string, string> = {
          INVALID_CREDENTIALS: 'Incorrect PIN',
          PIN_REQUIRED: 'No PIN set. Contact admin.',
        };
        setError(errorMap[result.error] || 'PIN verification failed');
      }
    } catch (err) {
      setError('Failed to verify PIN');
    } finally {
      setLoading(false);
    }
  }, [email, userId, onAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackToBadge = () => {
    setStep('badge');
    setUserId(null);
    setEmail('');
    setUserName('');
    setError(null);
    // useScannerInput re-focuses the input automatically when `disabled` flips
    // back to false on the badge step.
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
      {step === 'badge' && (
        <>
          {/* Badge scan icon */}
          <svg className="w-20 h-20 text-on-surface-variant mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
              d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
          </svg>

          <h2 className="text-on-surface text-xl font-semibold mb-2">Scan your badge</h2>
          <p className="text-on-surface-variant text-sm mb-6">Hold your badge barcode to the scanner</p>

          {error && (
            <p className="text-red-400 text-sm mb-4">{error}</p>
          )}

          {loading && (
            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mb-4" />
          )}

          {/* Hidden input for the badge scanner — driven by useScannerInput.
              NOTE: deliberately NO inputMode="none" here. On the rugged Android
              handhelds it suppressed the scanner's InputConnection — only the
              trailing Enter keydown got through, no character data, so badge
              scans silently failed. The Assembly and Marker inputs match this
              now; all three are plain. */}
          <input
            ref={badgeInputRef}
            className="opacity-0 absolute h-px w-px"
            autoComplete="off"
          />

          <div className="animate-pulse text-on-surface-variant text-xs mt-4">Waiting for scan...</div>

          {operators.length > 0 && (
            <div className="mt-8 w-full max-w-xs">
              <p className="text-on-surface-variant text-sm mb-2 text-center">Or pick your name</p>
              {lastOperator && (
                <button
                  type="button"
                  onClick={() => chooseOperator(lastOperator)}
                  className="mb-2 w-full min-h-[2.75rem] rounded-lg bg-gray-800 active:bg-gray-700 text-on-surface text-sm font-medium"
                >
                  {`Continue as ${lastOperator.display_name}`}
                </button>
              )}
              <UserPicker
                users={pickerUsers}
                onSelect={(u: any) => chooseOperator({ id: u.id, display_name: u.firstname })}
                inputProps={{ placeholder: 'Type your name', 'aria-label': 'Your name' }}
              />
            </div>
          )}
        </>
      )}

      {step === 'pin' && (
        <>
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
              {userName.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium">{userName}</p>
              {email && <p className="text-on-surface-variant text-xs">{email}</p>}
            </div>
          </div>

          <p className="text-on-surface-variant text-sm mb-6">Enter your 4-digit PIN</p>

          <PinPad onSubmit={handlePinSubmit} error={error} loading={loading} />

          <button
            onClick={handleBackToBadge}
            className="text-on-surface-variant hover:text-on-surface text-sm mt-6 underline"
          >
            Not you? Go back
          </button>
        </>
      )}
    </div>
  );
};

// =========================================================================
// SCANNER MODE (MAIN COMPONENT)
// =========================================================================

const ScannerMode: React.FC = () => {
  const pairsCompleted = useScannerStore((s: any) => s.pairsCompleted);
  const operator = useScannerStore((s: any) => s.operator);
  const setOperator = useScannerStore((s: any) => s.setOperator);
  const clearOperator = useScannerStore((s: any) => s.clearOperator);
  const recordPair = useScannerStore((s: any) => s.recordPair);
  const allowUntrackedBoards = useScannerStore((s: any) => s.allowUntrackedBoards);
  const setAllowUntrackedBoards = useScannerStore((s: any) => s.setAllowUntrackedBoards);

  const [deviceMode] = useDeviceMode();
  const firefleetUser = useUserStore((s: any) => s.user);

  // A new web release loads only while nobody is signed in (kiosk only).
  useIdleUpdate(!operator);

  // The open tool, null for home. Resets when the operator clears so a fresh
  // session always lands on home.
  const [toolId, setToolId] = useState<string | null>(null);
  const [toolParams, setToolParams] = useState<Record<string, unknown>>({});
  const tools = toolsFor({ kiosk: inShell(), user: firefleetUser });
  const tool = tools.find((t) => t.id === toolId) || null;

  // Personal-mode auto-auth: the JWT user is the operator. Inject them into the
  // scanner store so downstream code (which already keys off `operator`) doesn't
  // need to know about device mode. Shared mode keeps the existing badge+PIN flow
  // (operator gets populated by OperatorLoginGate.handleAuthenticated below).
  useEffect(() => {
    if (deviceMode === 'personal' && firefleetUser && !operator) {
      setOperator({
        id: firefleetUser.id,
        firstname: firefleetUser.firstname,
        lastname: firefleetUser.lastname,
        email: firefleetUser.email,
      });
    }
  }, [deviceMode, firefleetUser, operator, setOperator]);

  // Inactivity auto-lock: resets operator after 10 minutes of no interaction
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (operator) {
      inactivityTimer.current = setTimeout(() => {
        clearOperator();
      }, INACTIVITY_TIMEOUT_MS);
    }
  }, [operator, clearOperator]);

  // Start/restart timer when operator changes or on user interaction
  useEffect(() => {
    if (!operator) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return undefined;
    }

    resetInactivityTimer();

    const events = ['pointerdown', 'keydown'];
    events.forEach((evt) => window.addEventListener(evt, resetInactivityTimer));
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach((evt) => window.removeEventListener(evt, resetInactivityTimer));
    };
  }, [operator, resetInactivityTimer]);

  // Diagnostics before badge-in are anonymous, which is right:
  // a scan that fails AT the badge gate has no operator yet, and that is often
  // exactly the failure being chased.
  useEffect(() => {
    setDiagnosticOperator(operator?.id || null);
  }, [operator]);

  const handlePairComplete = useCallback((serialnumber: string) => {
    recordPair(serialnumber);
  }, [recordPair]);

  // The operator token (shell mode only) replaces the device token until the
  // operator clears. Set before setOperator so the next request already carries it.
  const handleAuthenticated = useCallback((user: any, operatorToken: string | undefined) => {
    shellTokens.setOperatorToken(operatorToken);
    setOperator(user);
  }, [setOperator]);

  useEffect(() => {
    if (!operator) shellTokens.clearOperatorToken();
  }, [operator]);

  const handleLock = useCallback(() => {
    clearOperator();
    leaveGuardRef.current = null;
    setToolId(null);
    setToolParams({});
  }, [clearOperator]);

  // When operator clears for any reason (lock button, inactivity timeout, badge
  // switch), drop back to home on next auth so the operator doesn't
  // surprise-land mid-workflow.
  useEffect(() => {
    if (!operator) {
      setToolId(null);
      setToolParams({});
    }
  }, [operator]);

  const leaveGuardRef = useRef<((leave: () => void) => boolean) | null>(null);
  const leaveTool = useCallback(() => {
    leaveGuardRef.current = null;
    setToolId(null);
    setToolParams({});
  }, []);
  const openTool = useCallback((id: string, params: Record<string, unknown> = {}) => {
    leaveGuardRef.current = null;
    setToolParams(params);
    setToolId(id);
  }, []);
  const ctx: ToolContext | null = operator ? {
    operatorId: operator.id,
    isSharedMode: deviceMode === 'shared',
    allowUntrackedBoards,
    setAllowUntrackedBoards,
    pairsCompleted,
    onPairComplete: handlePairComplete,
    setLeaveGuard: (guard) => { leaveGuardRef.current = guard; },
    openTool,
    toolParams,
  } : null;

  // In the kiosk, which scanner this is; everywhere else, which device mode.
  const scannerId = inShell() ? readScannerId() : '';
  const operatorShort = operator
    ? [operator.firstname, operator.lastname ? `${operator.lastname.charAt(0)}.` : ''].filter(Boolean).join(' ')
      || operator.email || 'Operator'
    : '';

  return (
    <div className="flex min-h-screen supports-[min-height:100dvh]:min-h-[100dvh] flex-col bg-surface">
      {/* Budgeted for the handheld's 424 CSS px: the logo is the one thing that
          gives way; the ID, battery and the operator's name never shrink. */}
      <header className="flex h-14 shrink-0 items-center gap-2 bg-surface-container-low px-3">
        <img src={instance.logo} alt={instance.logoAlt} className="h-6 w-auto min-w-0 shrink object-contain object-left" />
        {inShell() ? (
          scannerId && (
            <span className="shrink-0 whitespace-nowrap rounded bg-surface-container-highest px-1.5 py-0.5 font-mono text-xs tabular-nums text-on-surface-variant">
              <span className="sr-only">Scanner </span>{scannerId}
            </span>
          )
        ) : (
          <span className="shrink-0 whitespace-nowrap text-xs text-on-surface-variant">
            {deviceMode === 'shared' ? 'Shared device' : 'Personal device'}
          </span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-2.5">
          {inShell() && <ShellStatus />}
          {operator && (
            <>
              <span className="whitespace-nowrap text-xs text-on-surface">{operatorShort}</span>
              <button
                type="button"
                onClick={handleLock}
                className={`${BAR_KEY} bg-surface-container-highest px-3 text-on-surface`}
              >
                <LockIcon className="h-4 w-4" />
                Lock
              </button>
            </>
          )}
        </div>
      </header>

      {/* Content: login gate -> home -> the open tool */}
      {!operator ? (
        // Shared scanners require badge+PIN. Personal scanners auto-inject the JWT user
        // above; this branch is only reachable on a shared device or if the personal-mode
        // injection hasn't run yet.
        <OperatorLoginGate onAuthenticated={handleAuthenticated} />
      ) : !tool || !ctx ? (
        <ScannerHome
          firstname={operator.firstname || null}
          tools={tools}
          onSelect={(id) => openTool(id)}
          onLock={handleLock}
          operatorId={operator.id}
        />
      ) : (
        <>
          <div className="flex h-14 shrink-0 items-center gap-2 px-2">
            <button
              type="button"
              onClick={() => {
                const guard = leaveGuardRef.current;
                if (!guard || guard(leaveTool)) leaveTool();
              }}
              className={`${BAR_KEY} pl-1.5 pr-3 text-on-surface-variant hover:text-on-surface`}
            >
              <BackIcon className="h-5 w-5" />
              Tools
            </button>
            <h1 className="min-w-0 truncate text-base font-semibold text-on-surface">{tool.label}</h1>
            {tool.barControls && <div className="ml-auto shrink-0">{tool.barControls(ctx)}</div>}
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{tool.render(ctx)}</div>
        </>
      )}
    </div>
  );
};

export default ScannerMode;
